// src/components/messages/WorkplaceMessagesPanel.tsx
// Position-agnostic body for the workplace messages surface. Rendered by
// ActivityBannerPopover as a child section without any positioning of its own.
import { useEffect, useState } from "react";
import {
  getMessageAlertEnabled,
  setMessageAlertEnabled,
  subscribeMessageAlertSettings,
} from "@/lib/messages/messageAlertAudio";
import type { MemberRow, NotifRow, ParticipantReceipt, ThreadRow, ViewerInfo } from "./workplaceMessagesTypes";
import { WorkplaceThreadDetail } from "./WorkplaceThreadDetail";

export type WorkplaceMessagesPanelProps = {
  allowTestNotification: boolean;
  allowSenderLock: boolean;
  allowRequiresAck: boolean;
  errorText: string;
  unreadCount: number;
  unackedCount: number;
  testBusy: boolean;
  markBusy: boolean;
  replyBusy: boolean;
  archiveBusy: boolean;
  loadingList: boolean;
  threads: ThreadRow[];
  selectedThreadId: string | null;
  messages: NotifRow[];
  participantReceipts: ParticipantReceipt[];
  members: MemberRow[];
  viewer: ViewerInfo | null;
  recipientId: string;
  setRecipientId: (v: string) => void;
  newThreadTitle: string;
  setNewThreadTitle: (v: string) => void;
  newThreadText: string;
  setNewThreadText: (v: string) => void;
  newThreadSenderLocked: boolean;
  setNewThreadSenderLocked: (v: boolean) => void;
  newThreadRequiresAck: boolean;
  setNewThreadRequiresAck: (v: boolean) => void;
  newThreadAckDeadline: string;
  setNewThreadAckDeadline: (v: string) => void;
  replyText: string;
  setReplyText: (v: string) => void;
  replyRequiresAck: boolean;
  setReplyRequiresAck: (v: boolean) => void;
  onMarkAll: () => void;
  onTest: () => void;
  onCreateThread: () => void;
  onSendReply: () => void;
  onSelectThread: (threadId: string) => void;
  onMarkOne: (id: string) => void;
  onAcknowledge: (id: string) => void;
  onArchiveOne: (id: string) => void;
  onArchiveThread: (threadId: string) => void;
};

export function WorkplaceMessagesPanel(p: WorkplaceMessagesPanelProps) {
  return (
    <div className="text-left text-sm text-gray-800 dark:text-gray-100">
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-gray-900 dark:text-gray-50">Workplace messages</p>
        <div className="flex items-center gap-2">
          <MessageAlertToggle />
          <button
            type="button"
            disabled={p.markBusy || p.threads.length === 0}
            onClick={() => void p.onMarkAll()}
            className="text-[11px] font-medium text-blue-700 hover:underline disabled:opacity-40 dark:text-blue-300"
          >
            Mark all read
          </button>
        </div>
      </div>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
        Unread: {p.unreadCount} · Need ack: {p.unackedCount}
      </p>
      {p.errorText ? <p className="mt-1 text-[11px] text-red-600 dark:text-red-300">{p.errorText}</p> : null}

      {p.allowTestNotification ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={p.testBusy}
            onClick={() => void p.onTest()}
            className="rounded-lg border border-gray-300 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-800 hover:bg-gray-100 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-gray-100 dark:hover:bg-zinc-700"
          >
            {p.testBusy ? "Sending…" : "Send test notification"}
          </button>
        </div>
      ) : null}
      <div className="mt-3 rounded-xl border border-gray-200 p-2 dark:border-zinc-700">
        <p className="text-[11px] font-medium text-gray-700 dark:text-gray-200">New message</p>
        <div className="mt-2 grid grid-cols-1 gap-2">
          <select
            value={p.recipientId}
            onChange={(e) => p.setRecipientId(e.target.value)}
            className="rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
          >
            <option value="">Select recipient</option>
            {p.members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name} (L{m.trust_level})
              </option>
            ))}
          </select>
          <input
            value={p.newThreadTitle}
            onChange={(e) => p.setNewThreadTitle(e.target.value)}
            placeholder="Title (optional)"
            className="rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
          />
          <div className="flex gap-2">
            <input
              value={p.newThreadText}
              onChange={(e) => p.setNewThreadText(e.target.value)}
              placeholder="Start a new thread…"
              className="flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
            />
            <button
              type="button"
              disabled={p.replyBusy || !p.recipientId || !p.newThreadText.trim()}
              onClick={() => void p.onCreateThread()}
              className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
            >
              Start
            </button>
          </div>
          {p.allowRequiresAck ? (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={p.newThreadRequiresAck}
                  onChange={(e) => p.setNewThreadRequiresAck(e.target.checked)}
                  className="h-3 w-3"
                />
                Require acknowledgement
              </label>
              {p.newThreadRequiresAck ? (
                <label className="block text-[11px] text-gray-600 dark:text-gray-300">
                  Ack deadline (optional)
                  <input
                    type="datetime-local"
                    value={p.newThreadAckDeadline}
                    onChange={(e) => p.setNewThreadAckDeadline(e.target.value)}
                    className="mt-0.5 w-full rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
                  />
                </label>
              ) : null}
            </div>
          ) : null}
          {p.allowSenderLock ? (
            <label
              className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300"
              title="Recipients can't archive their copy until you've cleared yours."
            >
              <input
                type="checkbox"
                checked={p.newThreadSenderLocked}
                onChange={(e) => p.setNewThreadSenderLocked(e.target.checked)}
                className="h-3 w-3"
              />
              Lock until I close it
            </label>
          ) : null}
        </div>
      </div>

      {p.loadingList ? (
        <p className="mt-3 text-xs text-gray-500">Loading…</p>
      ) : p.threads.length === 0 ? (
        <p className="mt-3 text-xs text-gray-500">No workplace messages yet.</p>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-2">
          <ul className="max-h-44 divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700">
            {p.threads.map((t) => (
              <li key={t.thread_id}>
                <button
                  type="button"
                  onClick={() => {
                    p.onSelectThread(t.thread_id);
                  }}
                  className={`w-full px-2.5 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-zinc-800 ${
                    p.selectedThreadId === t.thread_id ? "bg-blue-50 dark:bg-zinc-800" : ""
                  }`}
                >
                  <div className="font-medium">{t.latest_message.title}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1">{t.latest_message.body}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400">
                    {t.unread_count} unread · {t.unacknowledged_count} need ack
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {p.selectedThreadId ? <WorkplaceThreadDetail p={p} /> : null}
        </div>
      )}
    </div>
  );
}

function MessageAlertToggle() {
  const [enabled, setEnabled] = useState<boolean>(() => getMessageAlertEnabled());
  useEffect(() => subscribeMessageAlertSettings(setEnabled), []);
  const label = enabled ? "Mute new-message ding" : "Unmute new-message ding";
  return (
    <button
      type="button"
      onClick={() => setMessageAlertEnabled(!enabled)}
      aria-label={label}
      title={label}
      className="text-[11px] text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
    >
      {enabled ? "🔔" : "🔕"}
    </button>
  );
}
